import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import RadioButton from './RadioButton';
import parse from 'html-react-parser';
import { getLabel, getTranslatedText } from '../pages/utils_v2/kumuHelper';
import CheckBoxButton from './CheckBoxButton';

Question.propTypes = {
    activity: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    handleChange: PropTypes.func.isRequired
};

function isHTML(str) {
    var doc = new DOMParser().parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
}

export default function Question({ activity, questionText, options, handleChange, selectedOption, selectedOptions }) {
    useEffect(() => {}, [selectedOption]);
    const { i18n } = useTranslation('translation');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const splitQuestion = (questionText) =>
        questionText !== undefined
            ? questionText.split('\n').map((item, i) => (
                <p key={i} style={{ marginTop: 0, fontSize: isMobile ? 16 : 20 }}>
                    <b>{item}</b>
                </p>
            ))
            : null;

    const questionFormat = (questionText) => (
        <p style={{ marginTop: 0, fontSize: isMobile ? 16 : 20 }}>
            <b>{parse(questionText)}</b>
        </p>
    );

    const handleSelectionChange = (e) => {
        const clickedValue = e.target.value;
        const clickedOption = JSON.parse(clickedValue);
        const clickedType = clickedOption.value.attributes['element type'];

        if (clickedType === 'statement') {
            handleChange(e, 'clearMultiples');
        } else if (clickedType === 'statement (multiple)') {
            handleChange(e, 'clearSingle');
        } else {
            handleChange(e);
        }
    };

    const sortedOptions = [...options].sort((a, b) => {
        const typeA = a.value.attributes['element type'];
        const typeB = b.value.attributes['element type'];
        if (typeA === 'statement (multiple)' && typeB === 'statement') return -1;
        if (typeA === 'statement' && typeB === 'statement (multiple)') return 1;
        return 0;
    });

    return (
        <Paper sx={{ backgroundColor: '#eaebed', borderRadius: '15px' }}>
            <div
                className="wb-frmvld fieldflow-lg wb-init wb-frmvld-inited wb-fieldflow-form"
                id="legislations"
            >
                <form
                    id="questionForm"
                    noValidate="novalidate"
                    onChange={(e) => handleSelectionChange(e)}
                >
                    <div className="mrgn-tp-md">
                        <div
                            id="legislations-list"
                            style={{ padding: isMobile ? '16px' : '25px' }}
                        >
                            <fieldset
                                id="legislation"
                                className='gc-chckbxrdio'
                                style={{ margin: 0, padding: 0, border: 'none' }}
                            >
                                {/* Activity legend — smaller on mobile */}
                                {activity !== undefined && (
                                    <legend style={{
                                        fontSize: isMobile ? '18px' : '20px',
                                        marginBottom: '8px',
                                        lineHeight: 1.4,
                                        width: '100%',
                                    }}>
                                        <i>{getTranslatedText(i18n.language, activity)}</i>
                                    </legend>
                                )}

                                {/* Question text */}
                                {isHTML(questionText)
                                    ? questionFormat(questionText)
                                    : splitQuestion(questionText)
                                }

                                {/* Options list */}
                                <ul
                                    className="list-unstyled lst-spcd-2"
                                    style={{
                                        // Tighter spacing between options on mobile
                                        marginBottom: 0,
                                        paddingLeft: isMobile ? '4px' : '0',
                                    }}
                                >
                                    {sortedOptions.map((option, index) => {
                                        const label = parse(getLabel(option, i18n.language));
                                        const value = JSON.stringify(option);
                                        const isMultiple = option.value.attributes['element type'] === 'statement (multiple)';

                                        return isMultiple ? (
                                            <CheckBoxButton
                                                key={index}
                                                index={index}
                                                label={label}
                                                selectedOptions={selectedOptions}
                                                value={value}
                                            />
                                        ) : (
                                            <RadioButton
                                                key={index}
                                                index={index}
                                                label={label}
                                                selectedOption={selectedOption}
                                                value={value}
                                            />
                                        );
                                    })}
                                </ul>
                            </fieldset>
                        </div>
                    </div>
                </form>
            </div>
        </Paper>
    );
}